import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import isEqual from 'lodash/isEqual';
import isEqualWith from 'lodash/isEqualWith';

import { useGetTunnelMessagesQuery } from 'api/main';
import { useSendAutoCertificationMutation } from 'api/tunnelSteps';
import { userSelectors } from 'reducers';
import { useProClient, useTunnelChangeHandlers } from 'utils/hooks';
import { jsonParseSafe, isStatusOk } from 'utils/helpers';
import { texts } from 'utils/languages/fr';

import { TunnelStep, TunnelBox, DocumentUpload } from '../common';
import { CustomTextField, RadioLabel, SubmitButton } from 'components';
import { Box, RadioGroup, Typography } from '@mui/material';

const subtitleSx = { color: 'common.black', flexGrow: 1 };
const questionSx = theme => ({
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
    [theme.breakpoints.down('sm')]: {
        alignItems: 'flex-start',
        flexDirection: 'column',
        marginTop: '10px',
    },
});

const otherOption = 'auto-certification.group_2.option_3';

const getAutoCertificationQuestions = data => {
    if (!data) return [];
    const stepKey = 'auto-certification';
    const stepKeys = Object.keys(data).filter(key => key.includes(stepKey));
    const messages = stepKeys.map(key => ({
        key,
        value: data[key],
    }));

    const questions = {};
    messages.forEach(msg => {
        const parts = msg.key.split('.');
        parts?.shift();
        const questionId = parts?.shift();
        const titleOptionOrLabel = parts?.shift();

        let question = questions[questionId];
        if (!question) {
            questions[questionId] = { key: null, value: null, options: [] };
        }

        if (titleOptionOrLabel === 'label') {
            questions[questionId].key = msg.key;
            questions[questionId].value = msg.value;
        } else if (titleOptionOrLabel.includes('option')) {
            questions[questionId].options.push({
                key: msg.key,
                value: msg.value,
                isOther: msg.key === 'auto-certification.group_2.option_3',
            });
        }
    });

    return Object.values(questions);
};

const AutoCertification = props => {
    const { handleNextStep, userStep } = props;

    const { data: messages } = useGetTunnelMessagesQuery();
    const [sendAutoCertification, { isLoading: investorLoader, isSuccess }] =
        useSendAutoCertificationMutation();
    const setTunnelChangesHandlers = useTunnelChangeHandlers();

    const internalUser = useSelector(userSelectors.getInternalUser);
    const tunnelSubscription = useSelector(userSelectors.getTunnelSubscription);

    const isResubmit = useMemo(() => userStep?.successfulAttemptsNumber > 0, [userStep]);
    const hasStatusOk = useMemo(() => isStatusOk(userStep), [userStep]);
    const questions = useMemo(() => getAutoCertificationQuestions(messages), [messages]);

    const certification = internalUser?.investorCompany?.certification;
    const fatcaDocuments = useMemo(
        () => internalUser?.investorCompany?.fatcaDocuments,
        [internalUser]
    );
    const [selectedFiles, setSelectedFiles] = useState(fatcaDocuments || []);

    const dacObject = useMemo(
        () => jsonParseSafe(certification?.DacCrsStatus, {}),
        [certification]
    );
    const fatcaObject = useMemo(
        () => jsonParseSafe(certification?.FatcaStatus, {}),
        [certification]
    );

    const fatcaStatutMemo = useMemo(() => {
        if (
            (fatcaObject.choice && !fatcaObject.choice[0] && !!fatcaObject.other) ||
            (!fatcaObject.choice && !!fatcaObject.other)
        ) {
            return otherOption;
        } else if (fatcaObject.choice) {
            return fatcaObject.choice[0] || '';
        }
    }, [fatcaObject]);

    const defaultCertification = useMemo(
        () => ({
            dacStatut: dacObject.choice ? dacObject.choice[0] || '' : '',
            fatcaStatut: fatcaStatutMemo,
            fatcaStatutOther: fatcaObject.other || '',
        }),
        [fatcaStatutMemo, dacObject, fatcaObject]
    );

    const [autoCertification, setAutoCertification] = useState(defaultCertification);
    const [submitDisabled, setSubmitDisabled] = useState(false);

    const isProClient = useProClient();
    const subscriptionIsTransfered =
        (tunnelSubscription.createdBy && tunnelSubscription.transferredToInvestorAt) ||
        !tunnelSubscription.createdBy;

    useEffect(() => setAutoCertification(defaultCertification), [defaultCertification]);

    const removeFile = useCallback(
        ({ currentTarget: { id } }) => {
            const documentFound = selectedFiles.find((file, i) => i === +id && file.id);
            if (documentFound) {
                const docId = documentFound.id;
                setSelectedFiles(prevState => {
                    return prevState.filter(file => file.id !== docId || !file.id);
                });
            } else {
                setSelectedFiles(selectedFiles.filter((_, i) => i !== +id));
            }
        },
        [selectedFiles]
    );

    const validateForm = useCallback(() => {
        if (!subscriptionIsTransfered) return true;
        const valid = isProClient || !!autoCertification.dacStatut;

        if (autoCertification.fatcaStatut === otherOption) {
            return valid && !!autoCertification.fatcaStatutOther;
        } else {
            return valid && !!autoCertification.fatcaStatut;
        }
    }, [autoCertification, isProClient, subscriptionIsTransfered]);

    const handleFileChange = ({ target: { files } }) => {
        const file = files[0];
        setSelectedFiles([file]);
    };

    const handleChange = useCallback(e => {
        const name = e.target.name;
        const value = e.target.value;
        switch (name) {
            case 'fatcaStatutOther':
                setAutoCertification(prevState => ({
                    ...prevState,
                    fatcaStatutOther: value,
                    fatcaStatut: otherOption,
                }));
                break;
            case 'fatcaStatut':
                setAutoCertification(prevState => ({
                    ...prevState,
                    fatcaStatut: value,
                    fatcaStatutOther: value === otherOption ? prevState.fatcaStatutOther : '',
                }));
                break;
            case 'dacStatut':
                setAutoCertification(prevState => ({
                    ...prevState,
                    dacStatut: value,
                }));
                break;
            default:
                break;
        }
    }, []);

    const handleSubmit = useCallback(
        afterSubmitCallback => () => {
            if (validateForm()) {
                const filesToRemove = fatcaDocuments
                    .filter(
                        document =>
                            !selectedFiles.find(selectedFile => selectedFile.id === document.id)
                    )
                    .map(document => document.id);

                sendAutoCertification({
                    payload: { autoCertification, selectedFiles, filesToRemove },
                    callback: afterSubmitCallback,
                });
            }
        },
        [validateForm, fatcaDocuments, sendAutoCertification, autoCertification, selectedFiles]
    );

    const checkChanges = useCallback(() => {
        if (!fatcaDocuments) return false;
        return (
            !isEqual(autoCertification, defaultCertification) ||
            !isEqualWith(selectedFiles, fatcaDocuments, (objValue, othValue) => {
                return (
                    objValue.length === othValue.length &&
                    objValue.every((formObject, i) => isEqual(formObject, othValue[i]))
                );
            })
        );
    }, [autoCertification, defaultCertification, selectedFiles, fatcaDocuments]);

    const changesMade = useMemo(() => checkChanges(), [checkChanges]);

    useEffect(
        () => setTunnelChangesHandlers(handleSubmit, checkChanges, isResubmit),
        [handleSubmit, checkChanges, isResubmit, setTunnelChangesHandlers]
    );

    useEffect(() => {
        if (validateForm()) {
            setSubmitDisabled(false);
        } else {
            setSubmitDisabled(true);
        }
    }, [validateForm]);

    useEffect(() => {
        if (isSuccess) {
            handleNextStep(true);
        }
    }, [handleNextStep, isSuccess]);

    const mappings = {
        'auto-certification.group_1.label': {
            name: 'dacStatut',
            value: autoCertification.dacStatut,
        },
        'auto-certification.group_2.label': {
            name: 'fatcaStatut',
            value: autoCertification.fatcaStatut,
        },
        'auto-certification.group_2.option_3': {
            name: 'fatcaStatutOther',
            value: autoCertification.fatcaStatutOther,
        },
    };

    const renderQuestion = question => {
        const showExtraOption = isProClient && mappings[question.key].name === 'dacStatut';
        return (
            <Box marginBottom={5} key={question.key}>
                <Typography
                    sx={{ ...subtitleSx, fontWeight: 'bold', marginBottom: '20px' }}
                    variant="body1"
                >
                    {question.value}
                </Typography>
                <Typography sx={subtitleSx} variant="body1">
                    {texts.PLEASE_CHOOSE_CATEGORY}
                </Typography>
                <RadioGroup
                    name={mappings[question.key].name}
                    value={mappings[question.key].value}
                    onChange={handleChange}
                    sx={{ display: 'flex', flexDirection: 'column', marginTop: '20px' }}
                >
                    {question.options.map(option => (
                        <Box key={option.key} sx={questionSx}>
                            <RadioLabel
                                key={option.key}
                                value={option.key}
                                label={option.value}
                                disabled={showExtraOption}
                                checked={
                                    mappings[question.key].value === option.key && !showExtraOption
                                }
                            />
                            {option.isOther && (
                                <CustomTextField
                                    formControlProps={{
                                        sx: theme => ({
                                            margin: 0,
                                            [theme.breakpoints.down('sm')]: { marginTop: '10px' },
                                        }),
                                    }}
                                    name={mappings[option.key].name}
                                    value={mappings[option.key].value}
                                    onChange={handleChange}
                                />
                            )}
                        </Box>
                    ))}
                    {showExtraOption && (
                        <Box sx={questionSx}>
                            <RadioLabel
                                value="other-option"
                                label={texts.FINANCIAL_INSTITUTION}
                                checked={showExtraOption}
                            />
                        </Box>
                    )}
                </RadioGroup>
            </Box>
        );
    };

    return (
        <TunnelStep title={texts.AUTO_CERTIFICATION}>
            <TunnelBox>
                <form onSubmit={e => e.preventDefault()}>{questions.map(renderQuestion)}</form>
                <Typography variant="body1">{texts.FATCA}</Typography>
                <DocumentUpload
                    id="auto-certification"
                    selectedFiles={selectedFiles}
                    handleFileChange={handleFileChange}
                    removeFile={removeFile}
                />
            </TunnelBox>
            <SubmitButton
                text={isResubmit && changesMade ? texts.SAVE_AND_CONTINUE : texts.SUIVANT}
                disabled={investorLoader || submitDisabled}
                loading={investorLoader}
                onClick={changesMade || !hasStatusOk ? handleSubmit() : handleNextStep}
            />
        </TunnelStep>
    );
};

AutoCertification.propTypes = {
    handleNextStep: PropTypes.func,
    userStep: PropTypes.object,
};

export default AutoCertification;
